import React from "react";
import { green } from "@material-ui/core/colors";
import { withStyles } from "@material-ui/core/styles";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";

import ConfirmationWrapper from "../components/confirmation_wrapper";

const styles = (theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
  },
  switchStyle: {
    margin: theme.spacing(1),
    color: "grey",
  },
});

const styleOverrideHalf = {
  minWidth: "50%",
  display: "inline-block",
  verticalAlign: "top",
};

const ColourSwitch = withStyles((theme) => ({
  switchBase: {
    color: theme.palette.primary.main,
    "&$checked": {
      color: theme.palette.primary.main,
    },
    "&$checked + $track": {
      backgroundColor: green[500],
    },
  },
  checked: {},
  track: {},
}))(Switch);

/********************************************
A Switch is a virtual DOM element implemented by React
under-the-hood using a checkbox input element.

- The state of a Switch is pseudo-stateless, state passes
through this component up to the parent event handler.
- By convention, Switch should have a unique `name` property
of their checkbox implementation.

@prop: confirmed := (Optional) an array of [bool, str, str] of;
        [add a confirmation onto this component?, confirmation title, confirmation message].
        e.g. [True, "Title", "Message].
        otherwise; [False, "", ""], [False], or False.
@prop: confirmSpecificStates := (Optional) a list of contentBuffer states to confirm on.
         if undefined ==> confirm all states (iff `confirmed` is defined).
         e.g. [True, False, ], [True], or [False]
*********************************************/
class SimpleSwitch extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      contentBuffer: props.switchOn,
    };
  }

  // The event handler for the Switch component itself, but not used when using Confirmation.
  handleChange(event) {
    if (this.state.contentBuffer === event.target.checked) {
      return; // i.e. no change.
    }

    if (this.usingConfirmations()) {
      return; // sometimes controlled via confirmation modal instead of this event handler.
    }

    this.bubbleStateUp.bind(this)(event.target.checked);
  }

  // i) A switch without confirmation will trigger this function,
  // ii) Otherwise, the Confirmation dialog "Confirm" button press triggers this function,
  //     without an argument in the ConfirmationWrapper context.
  bubbleStateUp(newContentBuffer) {
    if (this.usingConfirmations()) {
      newContentBuffer = !this.state.contentBuffer; // manually set the value.
    }

    this.setState(
      { contentBuffer: newContentBuffer }, // a switch can simply negate its state
      function () {
        this.props.onChange(
          this.props.url,
          this.props.attributeId,
          this.state.contentBuffer.toString()
        );
      }
    );
  }

  usingConfirmations() {
    // Return true iff Confirmation is turned on for this component.
    return (
      this.props.confirmed && this.props.confirmed[0] && this.confirmStates()
    );
  }

  confirmStates() {
    if (this.props.confirmSpecificStates === undefined) {
      return true; // default: confirmation on all contentBuffer states.
    }

    return this.props.confirmSpecificStates.includes(this.state.contentBuffer);
  }

  /**
    Allow for a Switch to have label prop of the format:
        - "Label" or,
        - ["Label"] or,
        - ["Label for state On", "Label for state Off"] or,
    **/
  labelMaker(switchOn, label) {
    if (typeof label === "string") {
      return label;
    }
    if (label instanceof Array) {
      if (label.length === 1) {
        return label[0];
      } else if (label.length === 2) {
        return switchOn ? label[0] : label[1];
      }
    }
    return "";
  }

  renderSwitch(classes) {
    return (
      <FormGroup variant="outlined" className={classes.switchStyle}>
        <FormControlLabel
          label={this.labelMaker(this.state.contentBuffer, this.props.label)}
          control={
            <ColourSwitch
              checked={this.state.contentBuffer}
              url={this.props.url}
              name={this.props.name}
              disabled={!this.props.editable}
              onChange={this.handleChange.bind(this)}
            />
          }
        />
      </FormGroup>
    );
  }

  render() {
    const { classes } = this.props;
    const widthStyleOverride =
      this.props.width === "half" ? styleOverrideHalf : null;

    return (
      <div
        key={this.props.key}
        className={classes.container}
        style={widthStyleOverride}
      >
        <ConfirmationWrapper
          confirmClick={this.bubbleStateUp.bind(this, this.props, undefined)}
          disableConfirmations={
            !this.props.editable || !this.usingConfirmations()
          }
          title={this.props.confirmed ? this.props.confirmed[1] : ""}
          confirmationMessage={
            this.props.confirmed ? this.props.confirmed[2] : ""
          }
        >
          {this.renderSwitch.bind(this)(classes)}
        </ConfirmationWrapper>
      </div>
    );
  }
}

export default withStyles(styles)(SimpleSwitch);
